import React from 'react';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';

import Table from 'components/customTable';

const ReactTooltip = dynamic(() => import('react-tooltip'), { ssr: false });

const legends = ['Male', 'Female', 'Unknown'];

const COLOR_CODES = ['#26A69A', '#5C6BC0', '#AB47BC'];

const propTypes = {
  jobTitle: PropTypes.string.isRequired,
  genderOverTime: PropTypes.arrayOf(
    PropTypes.shape({
      Male: PropTypes.number,
      Female: PropTypes.number,
      Unknown: PropTypes.number,
      year: PropTypes.number,
    })
  ).isRequired,
};

const GenderOverTime = ({ jobTitle, genderOverTime, genderOverTimeContentBlurb }) => {
  const tableData = (genderOverTime ?? []).map((x) => ({
    ...x,
    MaleDisplay: (100 * x.Male).toFixed(2) + '%',
    FemaleDisplay: (100 * x.Female).toFixed(2) + '%',
  }));

  const index =
    genderOverTime && genderOverTime.length && genderOverTime.findIndex((gender) => gender.Unknown);

  const isUnknown = index !== -1;

  const formatArrayForRange = genderOverTime.map((i) => parseInt(i.Male * 100));

  const defaultRange = { min: 0, max: 100 };

  const rangeYlabels = {
    min: Math.max(Math.min(...formatArrayForRange) - 5, defaultRange.min),
    max: Math.min(Math.max(...formatArrayForRange) + 5, defaultRange.max),
  };

  const diff = rangeYlabels.max - rangeYlabels.min;

  const generateYLabels = () => {
    const labelsCount = 5;
    const percentageStep = 100 / labelsCount;
    const yLabels = [];

    for (let i = 5; i >= 0; i--) {
      yLabels.push({ label: i * percentageStep });
    }

    return yLabels;
  };

  const yLabels = generateYLabels();

  const heightOfXLabel = (male) => {
    const maleColumnHeight = Number(male * 100).toFixed(1);
    const femaleColumnHeight = 100 - maleColumnHeight;

    return {
      Male: `${maleColumnHeight}%`,
      Female: `${femaleColumnHeight}%`,
    };
  };

  return (
    <section id="gender-over-time" className="gender-over-time-container">
      <div className="section-separator-medium" />

      <h3 className="cmpSectionh4 mb-3">{`${jobTitle} Gender Over Time`}</h3>

      {genderOverTimeContentBlurb && genderOverTimeContentBlurb.content && (
        <div
          className="mb-36"
          dangerouslySetInnerHTML={{ __html: genderOverTimeContentBlurb.content }}
        />
      )}

      <div className="bar-chart">
        <div className="d-flex flex-column flex-md-row justify-content-start justify-content-md-center legends">
          {legends.slice(0, isUnknown ? legends.length : 2).map((legend, idx) => (
            <div key={legend} className="legend">
              <div className="circle" style={{ background: COLOR_CODES[idx] }} />
              <span className="openSansBlack">{legend}</span>
            </div>
          ))}
        </div>
        <div className="grid">
          <div className="d-flex flex-column y-labels">
            {yLabels.map(({ label }) => (
              <div key={label} className="y-label">
                <div className="label lightTextStyle2">{label.toFixed(0)}%</div>
                <div className="grid-line" />
              </div>
            ))}
          </div>
          <div className="d-flex x-labels">
            {genderOverTime.map(({ year, Male, Female, Unknown }, idx) => {
              const heightOfXLabels = heightOfXLabel(Male);

              return (
                <div
                  key={year + idx}
                  className={`${
                    idx % 2 === 0 ? 'd-flex' : 'd-none'
                  } d-md-flex flex-column align-items-center x-label`}
                >
                  <div className="d-flex flex-column bar">
                    <div
                      data-tip={`<div><b>${year}</b></div><div>Male: <strong>${(100 * Male).toFixed(
                        2
                      )}%</strong></div><div>Female: <strong>${(100 * Female).toFixed(
                        2
                      )}%</strong></div>`}
                      data-place="top"
                      style={{ height: heightOfXLabels.Female, background: COLOR_CODES[1] }}
                      className={`${Unknown ? 'female' : ''}`}
                    />
                    <div
                      data-tip={`<div><b>${year}</b></div><div>Male: <strong>${(100 * Male).toFixed(
                        2
                      )}%</strong></div><div>Female: <strong>${(100 * Female).toFixed(
                        2
                      )}%</strong></div>`}
                      data-place="bottom"
                      style={{ height: heightOfXLabels.Male, background: COLOR_CODES[0] }}
                      className="male"
                    />
                  </div>
                  <div className="mt-2 label lightTextStyle2">{year}</div>
                </div>
              );
            })}
          </div>
          <ReactTooltip html />
        </div>
      </div>
      <div className="mb-0 z-mt-48">
        <Table
          columnConfig={{
            year: {
              columnName: 'Year',
              defaultSortDirection: 'desc',
            },
            MaleDisplay: {
              columnName: 'Male',
              customSortByField: 'Male',
            },
            FemaleDisplay: {
              columnName: 'Female',
              customSortByField: 'Female',
            },
          }}
          bodyData={tableData}
          defaultRowsDisplay={99}
          tableHeaderTitleContent={
            <h3 className="zp-title-h3 text-capitalize z-mb-24">{`${jobTitle} Gender By Year`}</h3>
          }
        />
      </div>
    </section>
  );
};

GenderOverTime.propTypes = propTypes;

export default GenderOverTime;
